import { h, Host } from "@stencil/core";
import { tokenMap } from "../../common/utils";
import { COMBO_BOX_EXPORT_PARTS, PAGINATOR_PARTS_DICTIONARY } from "../../common/reserved-names";
import { ACTUAL_PAGE, FIRST_ITEM_IN_PAGE, LAST_ITEM_IN_PAGE, TOTAL_ITEMS, TOTAL_PAGES, TOTAL_PAGES_UNDEFINED } from "./constants";
export class ChPaginatorRender {
    constructor() {
        this.actualPage = 1;
        this.hasHyperlink = false;
        this.totalPages = 1;
        this.controlsToRender = [];
        this.hasNextPage = false;
        this.itemsPerPage = 10;
        this.itemsPerPageOptions = undefined;
        this.maxPagesToShowLeft = 3;
        this.maxPagesToShowRight = 3;
        this.model = [];
        this.order = {
            itemsPerPage: 1,
            itemsPerPageInfo: 2,
            firstControl: 3,
            prevControl: 4,
            navigationControls: 5,
            nextControl: 6,
            lastControl: 7,
            navigationGoTo: 8,
            navigationControlsInfo: 9
        };
        this.selectedPage = 1;
        this.showFirstControl = false;
        this.showItemsPerPage = false;
        this.showItemsPerPageInfo = false;
        this.showLastControl = false;
        this.showNavigationControls = true;
        this.showNavigationControlsInfo = false;
        this.showNavigationGoTo = false;
        this.showNextControl = false;
        this.showPrevControl = false;
        this.totalItems = undefined;
        this.translations = {
            accessibleName: {
                currentPage: "current page, page ",
                goToPage: "go to page ",
                goToInput: "Go to page",
                itemsPerPageOptions: "Select amount of items to show",
                firstButton: "Go to first page",
                nextButton: "Go to next page",
                lastButton: "Go to last page",
                previousButton: "Go to previous page"
            },
            text: {
                goToButton: "Go",
                itemsPerPage: "Items per page:",
                of: "of ",
                ellipsis: "...",
                first: "First",
                prev: "Prev",
                next: "Next",
                last: "Last",
                unknownPages: "many",
                unknownItems: "many",
                showingItems: `Showing ${FIRST_ITEM_IN_PAGE} - ${LAST_ITEM_IN_PAGE} of ${TOTAL_ITEMS} items`,
                goToInputLabel: "Page:",
                showingPage: `Showing ${ACTUAL_PAGE} of ${TOTAL_PAGES} pages`
            }
        };
    }
    modelChanged() {
        this.hasHyperlink = Array.isArray(this.model);
        if (this.hasHyperlink) {
            this.totalPages = this.model?.length;
        }
        else {
            this.totalPages =
                this.model?.totalPages ??
                    TOTAL_PAGES_UNDEFINED;
        }
    }
    orderChanged() {
        this.#initializeControls();
    }
    selectedPageChanged() {
        if (this.hasHyperlink) {
            this.actualPage =
                typeof this.selectedPage === "number"
                    ? this.selectedPage
                    : this.model.indexOf(this.selectedPage) + 1;
        }
        else {
            this.actualPage = Number(this.selectedPage);
        }
    }
    /**
     * Event fired when there is a new page selection.
     */
    pageChange;
    /**
     * Event fired when the amount of items per page changes.
     */
    itemsPerPageChange;
    connectedCallback() {
        this.#initializeControls();
        this.hasHyperlink = Array.isArray(this.model);
        if (this.hasHyperlink) {
            this.totalPages = this.model.length;
        }
        else {
            this.totalPages =
                this.model?.totalPages ??
                    TOTAL_PAGES_UNDEFINED;
        }
    }
    #initializeControls = () => {
        const controls = [
            {
                name: "itemsPerPage",
                render: () => this.showItemsPerPage && this.#renderItemsPerPage()
            },
            {
                name: "itemsPerPageInfo",
                render: () => this.showItemsPerPageInfo && this.#renderItemsPerPageInfo()
            },
            {
                name: "navigationControls",
                render: () => this.showNavigationControls && this.#renderNavigationControls()
            },
            {
                name: "navigationGoTo",
                render: () => this.showNavigationGoTo && this.#renderNavigationGoTo()
            },
            {
                name: "navigationControlsInfo",
                render: () => this.showNavigationControlsInfo &&
                    this.#renderNavigationControlsInfo()
            },
            {
                name: "firstControl",
                render: () => this.showFirstControl && this.#renderFirstControl()
            },
            {
                name: "lastControl",
                render: () => this.showLastControl && this.#renderLastControl()
            },
            {
                name: "prevControl",
                render: () => this.showPrevControl && this.#renderPrevControl()
            },
            {
                name: "nextControl",
                render: () => this.showNextControl && this.#renderNextControl()
            }
        ];
        // Sort visible controls regarding their order value
        controls.sort((a, b) => {
            const orderA = this.order[a.name] ??
                Number.MAX_SAFE_INTEGER;
            const orderB = this.order[b.name] ??
                Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
        });
        this.controlsToRender = controls;
    };
    #getFirstItemInPage = () => `${this.actualPage * this.itemsPerPage - this.itemsPerPage + 1}`;
    #getLastItemInPage = () => {
        if (!this.totalItems ||
            this.totalItems > this.actualPage * this.itemsPerPage ||
            this.totalItems < 0) {
            return `${this.actualPage * this.itemsPerPage}`;
        }
        return `${this.totalItems}`;
    };
    #getTotalItemsString = () => !this.totalItems || this.totalItems < 0
        ? `${this.translations.text.unknownItems}`
        : `${this.totalItems}`;
    #getPaginationRange = () => {
        const { actualPage, maxPagesToShowLeft, maxPagesToShowRight } = this;
        const totalPages = this.totalPages;
        // Set array with pages to show, it starts with the first page on the array.
        // If page is an ellipsis, array number will be -1
        const pages = [1]; // Start with the first page
        // Check pages to display at the start and end of the navigation control
        let startPage = Math.max(actualPage - maxPagesToShowLeft, 2);
        let endPage = Math.min(actualPage + maxPagesToShowRight, totalPages - 1);
        // If not enough pages to show left, adjust the max pages to show
        if (actualPage - startPage < maxPagesToShowLeft) {
            startPage = Math.max(endPage - maxPagesToShowLeft - maxPagesToShowRight, 2);
        }
        // If not enough pages to show right, adjust the max pages to show
        if (endPage - actualPage < maxPagesToShowRight) {
            endPage = Math.min(startPage + maxPagesToShowLeft + maxPagesToShowRight, totalPages - 1);
        }
        // Add pages in range to the array
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        // Validate if ellipsis are needed in range
        if (totalPages > maxPagesToShowLeft + maxPagesToShowRight) {
            // Append ellipsis at start if necessary
            if (startPage > 2) {
                pages.splice(1, 0, -1); // Append ellipsis after the first page
            }
            // Append ellipsis at end if necessary
            if (endPage < totalPages - 1) {
                pages.push(-1); // Append ellipsis if there are pages after the last visible page
            }
        }
        // Append last page at the end if necessary
        if (totalPages > 1) {
            pages.push(totalPages); // Ensure the last page is always included
        }
        return pages;
    };
    #handleFirst = () => {
        this.actualPage = 1;
        this.pageChange.emit(this.actualPage);
    };
    #handleLast = () => {
        this.actualPage = this.totalPages;
        this.pageChange.emit(this.actualPage);
    };
    #handleNext = () => {
        this.actualPage += 1;
        this.pageChange.emit(this.actualPage);
    };
    #handlePrev = () => {
        this.actualPage -= 1;
        this.pageChange.emit(this.actualPage);
    };
    #handleItemsPerPageChange = (event) => {
        const select = event.target;
        const newItemsPerPage = Number(select.value);
        this.itemsPerPage = newItemsPerPage;
        this.itemsPerPageChange.emit(newItemsPerPage);
    };
    #handleGoToOnKeyDown = (event) => {
        if (event.key === "Enter") {
            this.#handleGotoPageChange(event);
        }
    };
    #handleGoToOnBlur = (event) => {
        this.#handleGotoPageChange(event);
    };
    #handleGotoPageChange = (event) => {
        const input = event.target;
        const page = Number(input.value);
        if (this.totalPages < 0) {
            this.pageChange.emit(page);
        }
        // Validate if page exceeds total pages range
        if (page >= 1 && page <= this.totalPages) {
            this.actualPage = Math.max(Math.min(page, this.totalPages), 1);
            this.pageChange.emit(this.actualPage);
        }
    };
    #handleSelectedPageChange = (event) => {
        event.stopPropagation();
        const elementRef = event.composedPath()[0];
        // Check the click event is performed in a button and anchor element
        if (elementRef.tagName.toLowerCase() !== "button" &&
            elementRef.tagName.toLowerCase() !== "a") {
            return;
        }
        event.preventDefault();
        let newPageSelected;
        if (elementRef.tagName.toLowerCase() === "a") {
            newPageSelected = this.hasHyperlink
                ? elementRef?.href
                : elementRef?.id;
        }
        else {
            newPageSelected = elementRef?.value;
        }
        // Don't fire the selectedItemChange event if the button is already selected
        if (this.selectedPage === newPageSelected) {
            return;
        }
        this.selectedPage = newPageSelected;
        const eventInfo = this.pageChange.emit(this.actualPage);
        if (!eventInfo.defaultPrevented) {
            this.selectedPage = newPageSelected;
        }
    };
    #renderItemsPerPage = () => (h("form", { class: "items-per-page", part: PAGINATOR_PARTS_DICTIONARY.ITEMS_PER_PAGE }, this.translations.text.itemsPerPage && (h("label", { htmlFor: "items-per-page", part: PAGINATOR_PARTS_DICTIONARY.ITEMS_PER_PAGE__LABEL }, this.translations.text.itemsPerPage)), h("ch-combo-box-render", { class: "combo-box", id: "items-per-page", name: "items-per-page", model: this.itemsPerPageOptions, onChange: this.#handleItemsPerPageChange, hostParts: PAGINATOR_PARTS_DICTIONARY.ITEMS_PER_PAGE__COMBO_BOX, value: this.itemsPerPage.toString(), exportparts: COMBO_BOX_EXPORT_PARTS, accessibleName: this.translations.accessibleName.itemsPerPageOptions })));
    #renderItemsPerPageInfo = () => (h("div", { class: "items-per-page-info", part: PAGINATOR_PARTS_DICTIONARY.ITEMS_PER_PAGE_INFO }, h("span", { part: PAGINATOR_PARTS_DICTIONARY.ITEMS_PER_PAGE_INFO__TEXT }, this.translations.text.showingItems
        .replace(FIRST_ITEM_IN_PAGE, this.#getFirstItemInPage())
        .replace(LAST_ITEM_IN_PAGE, this.#getLastItemInPage())
        .replace(TOTAL_ITEMS, this.#getTotalItemsString()))));
    #renderFirstControl = () => (h("button", { "aria-label": this.translations.text.first
            ? undefined
            : this.translations.accessibleName.firstButton, class: "first-button", part: tokenMap({
            [PAGINATOR_PARTS_DICTIONARY.FIRST]: true,
            [PAGINATOR_PARTS_DICTIONARY.DISABLED]: this.actualPage === 1
        }), disabled: this.actualPage === 1, onClick: this.actualPage !== 1 && this.#handleFirst, type: "button" }, this.translations.text.first));
    #renderLastControl = () => (h("button", { "aria-label": this.translations.text.last
            ? undefined
            : this.translations.accessibleName.lastButton, class: "last-button", part: tokenMap({
            [PAGINATOR_PARTS_DICTIONARY.LAST]: true,
            [PAGINATOR_PARTS_DICTIONARY.DISABLED]: this.actualPage === this.totalPages
        }), onClick: (this.actualPage !== this.totalPages || this.hasNextPage) &&
            this.#handleLast, disabled: this.actualPage === this.totalPages, type: "button" }, this.translations.text.last));
    #renderNextControl = () => (h("button", { "aria-label": this.translations.text.next
            ? undefined
            : this.translations.accessibleName.nextButton, class: "next-button", part: tokenMap({
            [PAGINATOR_PARTS_DICTIONARY.NEXT]: true,
            [PAGINATOR_PARTS_DICTIONARY.DISABLED]: this.actualPage === this.totalPages
        }), disabled: this.actualPage === this.totalPages, onClick: (this.actualPage !== this.totalPages || this.hasNextPage) &&
            this.#handleNext, type: "button" }, this.translations.text.next));
    #renderPrevControl = () => (h("button", { "aria-label": this.translations.text.prev
            ? undefined
            : this.translations.accessibleName.previousButton, class: "prev-button", part: tokenMap({
            [PAGINATOR_PARTS_DICTIONARY.PREV]: true,
            [PAGINATOR_PARTS_DICTIONARY.DISABLED]: this.actualPage === 1
        }), disabled: this.actualPage === 1, onClick: this.actualPage !== 1 && this.#handlePrev, type: "button" }, this.translations.text.prev));
    #renderNavigationControls = () => this.hasHyperlink ||
        this.model?.urlMapping ? (h("nav", { class: "pages", onClick: this.#handleSelectedPageChange, part: PAGINATOR_PARTS_DICTIONARY.PAGES }, h("ol", { class: "navList" }, this.#getPaginationRange().map(this.#renderPaginatorItem)))) : (h("ol", { class: "pages", onClick: this.#handleSelectedPageChange, part: PAGINATOR_PARTS_DICTIONARY.PAGES }, this.#getPaginationRange().map(this.#renderPaginatorItem)));
    #renderNavigationControlsInfo = () => (h("div", { part: PAGINATOR_PARTS_DICTIONARY.NAVIGATION_INFO, class: "navigation-info" }, h("span", { part: PAGINATOR_PARTS_DICTIONARY.NAVIGATION_INFO__TEXT }, this.translations.text.showingPage
        .replace(ACTUAL_PAGE, this.actualPage?.toString())
        .replace(TOTAL_PAGES, (this.totalPages > 0 && this.totalPages?.toString()) ||
        this.translations.text.unknownPages))));
    #renderNavigationGoTo = () => (h("div", { part: PAGINATOR_PARTS_DICTIONARY.GO_TO, class: "go-to" }, this.translations.text.goToInputLabel && (h("label", { htmlFor: "goto-input", part: PAGINATOR_PARTS_DICTIONARY.GO_TO__LABEL }, this.translations.text.goToInputLabel)), h("input", { id: "goto-input", part: PAGINATOR_PARTS_DICTIONARY.GO_TO__INPUT, type: "number", min: "1", max: this.totalPages, value: this.actualPage, onKeyDown: this.#handleGoToOnKeyDown, onBlur: this.#handleGoToOnBlur, "aria-label": !this.translations.accessibleName.goToInput ||
            this.translations.text.goToInputLabel ===
                this.translations.accessibleName.goToInput
            ? undefined
            : this.translations.accessibleName.goToInput }), h("span", null, this.translations.text.of, this.translations.text.of &&
        ((this.totalPages > 0 && this.totalPages?.toString()) ||
            this.translations.text.unknownPages))));
    #renderPaginatorItem = (page, index) => {
        if (page > 0) {
            // Validate if pagination has hyperlink
            if (this.hasHyperlink) {
                return (h("li", null, h("a", { "aria-current": this.actualPage === page ? "page" : undefined, "aria-label": this.actualPage === page
                        ? `${this.translations.accessibleName.currentPage}${page}`
                        : `${this.translations.accessibleName.goToPage}${page}`, key: index, part: tokenMap({
                        [PAGINATOR_PARTS_DICTIONARY.PAGE]: true,
                        [PAGINATOR_PARTS_DICTIONARY.PAGE_SELECTED]: this.actualPage === page,
                        [PAGINATOR_PARTS_DICTIONARY.PAGE_NOT_SELECTED]: this.actualPage !== page
                    }), href: this.model[page - 1] }, page)));
            }
            // Validate if pagination has urlMapping, if true, return anchor element
            if (this.model?.urlMapping) {
                return (h("li", null, h("a", { "aria-current": this.actualPage === page ? "page" : undefined, "aria-label": this.actualPage === page
                        ? `${this.translations.accessibleName.currentPage}${page}`
                        : `${this.translations.accessibleName.goToPage}${page}`, key: index, id: page.toString(), part: tokenMap({
                        [PAGINATOR_PARTS_DICTIONARY.PAGE]: true,
                        [PAGINATOR_PARTS_DICTIONARY.PAGE_SELECTED]: this.actualPage === page,
                        [PAGINATOR_PARTS_DICTIONARY.PAGE_NOT_SELECTED]: this.actualPage !== page
                    }), href: this.model.urlMapping(page) }, page)));
            }
            // Else, return a button with page number as value
            return (h("li", null, h("button", { "aria-current": this.actualPage === page ? "page" : undefined, "aria-label": this.actualPage === page
                    ? `${this.translations.accessibleName.currentPage}${page}`
                    : `${this.translations.accessibleName.goToPage}${page}`, key: index, part: tokenMap({
                    [PAGINATOR_PARTS_DICTIONARY.PAGE]: true,
                    [PAGINATOR_PARTS_DICTIONARY.PAGE_SELECTED]: this.actualPage === page,
                    [PAGINATOR_PARTS_DICTIONARY.PAGE_NOT_SELECTED]: this.actualPage !== page
                }), value: page, type: "button" }, page)));
        }
        // if page is "-1", it renders a span with the ellipsis
        return (h("li", null, h("span", { key: index, part: tokenMap({
                [PAGINATOR_PARTS_DICTIONARY.PAGE]: true,
                [PAGINATOR_PARTS_DICTIONARY.ELLIPSIS]: true
            }) }, this.translations.text.ellipsis)));
    };
    render() {
        return (h(Host, { key: '3d92f2626e8103d6258b3e61eef28dab1a3d0ef2' }, this.controlsToRender.map(control => control.render())));
    }
    static get is() { return "ch-paginator-render"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["paginator-render.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["paginator-render.css"]
        };
    }
    static get properties() {
        return {
            "hasNextPage": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates that the end has been reached.\nUse when total pages are not known (totalPages = -1)."
                },
                "attribute": "has-next-page",
                "reflect": false,
                "defaultValue": "false"
            },
            "itemsPerPage": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Number for Total Items Per Page."
                },
                "attribute": "items-per-page",
                "reflect": false,
                "defaultValue": "10"
            },
            "itemsPerPageOptions": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ComboBoxModel",
                    "resolved": "ComboBoxItemModel[]",
                    "references": {
                        "ComboBoxModel": {
                            "location": "import",
                            "path": "../combo-box/types",
                            "id": "src/components/combo-box/types.ts::ComboBoxModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the options in Items Per Page combo-box."
                }
            },
            "maxPagesToShowLeft": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Number of pages to show starting from the selected page to the left."
                },
                "attribute": "max-pages-to-show-left",
                "reflect": false,
                "defaultValue": "3"
            },
            "maxPagesToShowRight": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Number of pages to show starting from the selected page to the right."
                },
                "attribute": "max-pages-to-show-right",
                "reflect": false,
                "defaultValue": "3"
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "PaginatorRenderModel",
                    "resolved": "string[] | ({ totalPages?: number; urlMapping?: (index: number) => string; })",
                    "references": {
                        "PaginatorRenderModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/paginator-render/types.ts::PaginatorRenderModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the UI Model for the Paginator."
                },
                "defaultValue": "[]"
            },
            "order": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "PaginatorControlsOrder",
                    "resolved": "{ itemsPerPage?: number; itemsPerPageInfo?: number; firstControl?: number; lastControl?: number; prevControl?: number; nextControl?: number; navigationControls?: number; navigationGoTo?: number; navigationControlsInfo?: number; }",
                    "references": {
                        "PaginatorControlsOrder": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/paginator-render/types.ts::PaginatorControlsOrder"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the order for the controls."
                },
                "defaultValue": "{\n    itemsPerPage: 1,\n    itemsPerPageInfo: 2,\n    firstControl: 3,\n    prevControl: 4,\n    navigationControls: 5,\n    nextControl: 6,\n    lastControl: 7,\n    navigationGoTo: 8,\n    navigationControlsInfo: 9\n  }"
            },
            "selectedPage": {
                "type": "any",
                "mutable": true,
                "complexType": {
                    "original": "string | number",
                    "resolved": "number | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the Selected Page, can be a number or hyperlink."
                },
                "attribute": "selected-page",
                "reflect": false,
                "defaultValue": "1"
            },
            "showFirstControl": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the navigation First control."
                },
                "attribute": "show-first-control",
                "reflect": false,
                "defaultValue": "false"
            },
            "showItemsPerPage": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the Items Per Page control."
                },
                "attribute": "show-items-per-page",
                "reflect": false,
                "defaultValue": "false"
            },
            "showItemsPerPageInfo": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the Items Per Page Info."
                },
                "attribute": "show-items-per-page-info",
                "reflect": false,
                "defaultValue": "false"
            },
            "showLastControl": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the navigation Last control."
                },
                "attribute": "show-last-control",
                "reflect": false,
                "defaultValue": "false"
            },
            "showNavigationControls": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the navigation controls."
                },
                "attribute": "show-navigation-controls",
                "reflect": false,
                "defaultValue": "true"
            },
            "showNavigationControlsInfo": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the navigation Info."
                },
                "attribute": "show-navigation-controls-info",
                "reflect": false,
                "defaultValue": "false"
            },
            "showNavigationGoTo": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the navigation Go To control."
                },
                "attribute": "show-navigation-go-to",
                "reflect": false,
                "defaultValue": "false"
            },
            "showNextControl": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the navigation Next control."
                },
                "attribute": "show-next-control",
                "reflect": false,
                "defaultValue": "false"
            },
            "showPrevControl": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render the navigation Prev control."
                },
                "attribute": "show-prev-control",
                "reflect": false,
                "defaultValue": "false"
            },
            "totalItems": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number | undefined",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Number for Total Items, default = undefined."
                },
                "attribute": "total-items",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "translations": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "PaginatorTranslations",
                    "resolved": "{ accessibleName: { currentPage: string; goToPage: string; goToInput: string; itemsPerPageOptions: string; firstButton: string; lastButton: string; nextButton: string; previousButton: string; }; text: { first: string; goToButton: string; of: string; itemsPerPage: string; ellipsis: string; last: string; next: string; goToInputLabel: string; prev: string; showingItems: string; showingPage: string; unknownPages: string; unknownItems: string; }; }",
                    "references": {
                        "PaginatorTranslations": {
                            "location": "import",
                            "path": "./translations",
                            "id": "src/components/paginator-render/translations.ts::PaginatorTranslations"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the literals required in the control."
                },
                "defaultValue": "{\n    accessibleName: {\n      currentPage: \"current page, page \",\n      goToPage: \"go to page \",\n      goToInput: \"Go to page\",\n      itemsPerPageOptions: \"Select amount of items to show\",\n      firstButton: \"Go to first page\",\n      nextButton: \"Go to next page\",\n      lastButton: \"Go to last page\",\n      previousButton: \"Go to previous page\"\n    },\n    text: {\n      goToButton: \"Go\",\n      itemsPerPage: \"Items per page:\",\n      of: \"of \",\n      ellipsis: \"...\",\n      first: \"First\",\n      prev: \"Prev\",\n      next: \"Next\",\n      last: \"Last\",\n      unknownPages: \"many\",\n      unknownItems: \"many\",\n      showingItems: `Showing ${FIRST_ITEM_IN_PAGE} - ${LAST_ITEM_IN_PAGE} of ${TOTAL_ITEMS} items`,\n      goToInputLabel: \"Page:\",\n      showingPage: `Showing ${ACTUAL_PAGE} of ${TOTAL_PAGES} pages`\n    }\n  }"
            }
        };
    }
    static get states() {
        return {
            "actualPage": {},
            "hasHyperlink": {},
            "totalPages": {},
            "controlsToRender": {}
        };
    }
    static get events() {
        return [{
                "method": "pageChange",
                "name": "pageChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event fired when there is a new page selection."
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }, {
                "method": "itemsPerPageChange",
                "name": "itemsPerPageChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event fired when the amount of items per page changes."
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "model",
                "methodName": "modelChanged"
            }, {
                "propName": "order",
                "methodName": "orderChanged"
            }, {
                "propName": "selectedPage",
                "methodName": "selectedPageChanged"
            }];
    }
}
